// src/app/core/theme/theme.service.ts
import { Injectable, signal, inject, DestroyRef } from '@angular/core';
import { TenantService } from '../tenant/tenant.service';
import { FeatureFlagService } from '../feature-flags/feature-flag.service';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'bel_theme';
const VALID_MODES: ReadonlySet<string> = new Set(['light', 'dark', 'system']);

// Coinciden con --surface-raised de _variables.scss (el fondo real del
// topbar, que es lo que el usuario ve pegado a la barra del navegador).
// No pueden ser var(--...): <meta content="..."> exige un valor literal.
const META_THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#FFFFFF',
  dark:  '#24272C',
};

/**
 * ThemeService — modo oscuro (Prompt 17).
 *
 * REGLA: `data-theme` se escribe únicamente en `document.documentElement`,
 * nunca en `<body>` ni con estilos inline por componente (es una decisión de
 * cascada CSS, no un control de seguridad — ver dictamen A4 del Prompt 17).
 *
 * DEFECTO SIN PREFERENCIA GUARDADA: 'light', no 'system'. Los usuarios
 * nuevos ven siempre claro, sin heredar la preferencia del sistema
 * operativo — decisión explícita del operador. 'system' sigue existiendo en
 * el tipo y se resuelve correctamente si llega a estar guardado (por
 * ejemplo, de una instalación previa a este cambio), pero ya no es
 * alcanzable como valor inicial de fábrica ni desde ningún control de la UI.
 *
 * CASO `features.darkMode: false` con `localStorage[bel_theme]` guardado de
 * una sesión anterior (3.1): `resolveTheme()` fuerza 'light' siempre que la
 * bandera esté desactivada, sin importar `mode`. `theme-boot.js` no conoce
 * la bandera (corre antes de que ConfigService la lea) y puede pintar
 * oscuro por una fracción de segundo si había 'dark' guardado — es el único
 * parpadeo aceptado por el prompt para este escenario de desactivación; se
 * corrige aquí, al construirse este servicio, antes de que el usuario vea
 * la aplicación interactiva.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly tenantService = inject(TenantService);
  private readonly featureFlags  = inject(FeatureFlagService);
  private readonly destroyRef    = inject(DestroyRef);

  private readonly _mode     = signal<ThemeMode>('light');
  private readonly _resolved = signal<ResolvedTheme>('light');

  /** Preferencia del usuario — puede ser 'system' */
  readonly mode = this._mode.asReadonly();
  /** Lo que se está mostrando realmente — nunca 'system' */
  readonly resolved = this._resolved.asReadonly();

  private mediaQuery: MediaQueryList | null = null;

  constructor() {
    const initialMode = this.readStoredMode();
    this._mode.set(initialMode);
    this._resolved.set(this.resolveTheme(initialMode));
    this.applyResolvedTheme();
    this.setupSystemListener();
    this.setupCrossTabSync();
  }

  setMode(mode: ThemeMode): void {
    this._mode.set(mode);
    this._resolved.set(this.resolveTheme(mode));
    this.persistMode(mode);
    this.applyResolvedTheme();
  }

  /**
   * Semántica fijada en 0.6 bis del Prompt 17: desde 'system', conmuta al
   * OPUESTO de lo que se está mostrando (`resolved()`) y fija `mode` a ese
   * valor explícito — a partir de ahí se abandona el seguimiento del SO
   * hasta que se vuelva a elegir 'system' explícitamente (no ofrecido desde
   * la UI en este prompt; ver decisión 0.6 bis, opción (a)).
   */
  toggle(): void {
    const next: ResolvedTheme = this._resolved() === 'dark' ? 'light' : 'dark';
    this.setMode(next);
  }

  private resolveTheme(mode: ThemeMode): ResolvedTheme {
    // features.darkMode: false → la app queda en claro, sin excepción.
    if (!this.featureFlags.isEnabled('darkMode')) return 'light';
    if (mode === 'light' || mode === 'dark') return mode;
    return this.prefersDark() ? 'dark' : 'light';
  }

  private prefersDark(): boolean {
    try {
      return typeof window !== 'undefined' && !!window.matchMedia
        && window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch {
      return false;
    }
  }

  private readStoredMode(): ThemeMode {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      stored = null;
    }
    // Un valor no reconocido (localStorage manipulado a mano) se descarta:
    // no se propaga como preferencia válida. Sin preferencia guardada, el
    // valor por defecto es 'light' — decisión explícita del operador: los
    // usuarios nuevos NO heredan la preferencia de tema del sistema
    // operativo, ven claro hasta que activen el toggle.
    return stored && VALID_MODES.has(stored) ? (stored as ThemeMode) : 'light';
  }

  private persistMode(mode: ThemeMode): void {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // EXCEPCIÓN CONTROLADA a R14: localStorage solo para la preferencia de
      // tema. No contiene datos de sesión ni información sensible del
      // usuario. Autorizada en el Prompt 17 (enmienda a R14). Valores
      // posibles: 'light' | 'dark' | 'system'.
      //
      // Si falla (navegación privada, cookies de terceros bloqueadas,
      // política corporativa): el tema sigue aplicado en memoria y en el DOM
      // durante la sesión; solo no persiste entre recargas. No relanzar.
    }
  }

  private applyResolvedTheme(): void {
    const resolved = this._resolved();
    document.documentElement.setAttribute('data-theme', resolved);
    this.updateMetaThemeColor(resolved);
    // Reaplica el color de marca de la institución para el tema activo
    // (usa institution.primaryColorDark si el operador lo definió).
    this.tenantService.applyTheme(resolved);
  }

  private updateMetaThemeColor(resolved: ResolvedTheme): void {
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', META_THEME_COLOR[resolved]);
  }

  private setupSystemListener(): void {
    try {
      if (typeof window === 'undefined' || !window.matchMedia) return;
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handler = (event: MediaQueryListEvent): void => {
        // Solo reacciona en modo 'system' — en modo explícito, un cambio del
        // SO no debe alterar lo que el usuario eligió activamente.
        if (this._mode() !== 'system') return;
        if (!this.featureFlags.isEnabled('darkMode')) return;
        this._resolved.set(event.matches ? 'dark' : 'light');
        this.applyResolvedTheme();
      };

      this.mediaQuery.addEventListener('change', handler);
      this.destroyRef.onDestroy(() => this.mediaQuery?.removeEventListener('change', handler));
    } catch {
      // Sin matchMedia no hay reacción en vivo a cambios del SO; el tema
      // resuelto al arrancar sigue siendo válido.
    }
  }

  /**
   * Sincronización entre pestañas (3.3 del Prompt 17). El evento `storage`
   * es NATIVO del navegador y se dispara solo en las pestañas que NO
   * originaron el cambio — no hace falta lógica para evitar un eco. No
   * reutiliza el `BroadcastChannel` de `LOGOUT` de SessionService a
   * propósito: mezclar señales de sesión con señales de presentación en el
   * mismo canal es el tipo de acoplamiento que después causa un logout
   * espurio.
   */
  private setupCrossTabSync(): void {
    try {
      if (typeof window === 'undefined') return;

      const handler = (event: StorageEvent): void => {
        if (event.key !== STORAGE_KEY) return;
        // Mismo criterio que readStoredMode(): valor ausente o no
        // reconocido (ej. localStorage.clear() en la otra pestaña, o un
        // valor manipulado a mano) cae a 'light', nunca se propaga tal cual.
        const nextMode = event.newValue && VALID_MODES.has(event.newValue)
          ? (event.newValue as ThemeMode)
          : 'light';
        // No se vuelve a escribir en localStorage: la pestaña de origen ya
        // lo hizo — esta solo refleja el cambio en memoria y en el DOM.
        this._mode.set(nextMode);
        this._resolved.set(this.resolveTheme(nextMode));
        this.applyResolvedTheme();
      };

      window.addEventListener('storage', handler);
      this.destroyRef.onDestroy(() => window.removeEventListener('storage', handler));
    } catch {
      // Sin sincronización entre pestañas si algo falla — cada pestaña
      // sigue funcionando de forma independiente con su propia preferencia.
    }
  }
}
